import _ from "lodash";
import React, { useRef } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";

import {
  getStaffCoursesQuery,
  getCurriculumCoursesQuery,
  getCurriculumLearningCoursesQuery,
} from "AppComponents/CourseSelection/modules/Queries";
import { getStaffCoursesQueryFromCache } from "AppComponents/CourseSelection/modules/GraphqlHelpers";
import classes from "./CourseList.scss";
import {
  checkHasLibrarySuperAdminAccess,
  getCurriculumProgramsWithAllClassAccess,
  checkTermsExistsForSelectedCurriculumProgram,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import { isToddleLibraryRouteBySegment as isToddleLibraryRoute } from "Utils/RouteMatchUtils";
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";
import Loading from "UIComponents/Loading";
import MoreItemWithToolTip from "UIComponents/MoreItemWithToolTip";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { useI18n } from "Hooks";
import {
  getAvatarColor,
  onEnterOrSpaceKeyDown,
  prepareLoadMoreFeed,
} from "Utils";
import { useHandleScroll } from "Utils/CustomHooks";
import { EmptyState, Avatar, RadioButton, Tooltip } from "@toddle-edu/ds-web";
import { NoSearchResultsIllustration } from "@toddle-edu/ds-theme";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";

const CourseItem = ({
  id,
  title,
  grades,
  subjects,
  isSelected,
  handleSelect,
  isTermsEnabled,
  shouldDisplaySubjects,
  t,
}) => {
  const cellTextClasses = classNames("text-body-1", classes.title, {
    [classes.disabled]: !isTermsEnabled,
  });

  const onCourseSelection = () => {
    if (isTermsEnabled) {
      handleSelect();
    }
  };

  const courseItemClasses = classNames(classes.courseItem, {
    [classes.courseItemWithSubjects]: shouldDisplaySubjects,
  });

  return (
    <Tooltip
      tooltip={t("classRoom:import_from_label_no_grading_periods_tooltip", {
        label: t("common:toddle_library"),
      })}
      visible={!isTermsEnabled}
    >
      <div
        role="button"
        tabIndex={0}
        className={courseItemClasses}
        onClick={onCourseSelection}
        data-test-id={`appComponents-courseList-item-${id}`}
        onKeyDown={e => {
          onEnterOrSpaceKeyDown(e, onCourseSelection);
        }}
      >
        <div className={classes.radioButtonContainer}>
          <RadioButton
            checked={isSelected}
            disabled={!isTermsEnabled}
            name="courseList-radiobutton"
            aria-label={t("common:select_class_with_classname", {
              className: title,
            })}
          />
        </div>

        <div className={classes.classNameContainer}>
          <Avatar
            size="small"
            color={getAvatarColor(id)}
            shade="light"
            shape="square"
            name={title}
          />
          <div className={cellTextClasses} dir="auto">
            {title}
          </div>
        </div>

        <div className={cellTextClasses} dir="auto">
          <MoreItemWithToolTip
            items={_.map(grades, grade => grade.name)}
            length={1}
            testId={`appComponents-courseList-item-${id}-grade`}
          />
        </div>
        {shouldDisplaySubjects ? (
          <div className={cellTextClasses} dir="auto">
            <MoreItemWithToolTip
              items={_.map(subjects, "name")}
              length={1}
              testId={`appComponents-courseList-item-${id}-subject`}
            />
          </div>
        ) : null}
      </div>
    </Tooltip>
  );
};

const CourseList = ({
  courses,
  selectedIds,
  handleSelect,
  isLoading,
  networkStatus,
  loadMoreFeed,
  hasNextPage,
  containerRef,
  isDPLibrary = false,
  selectedGradeId,
  selectedCurriulumId,
  checkTermsExistsForSelectedCurriculumProgram,
  shouldCheckIfTermsExist,
  shouldDisplaySubjects = false,
  isToddleLibrary2_0,
  loadingCurriculums,
}) => {
  const isTermsEnabled =
    !shouldCheckIfTermsExist ||
    checkTermsExistsForSelectedCurriculumProgram({
      selectedCurriculumProgramId: selectedCurriulumId,
    });
  const listContainerRef = useRef(null);

  if (isDPLibrary) {
    courses = _.filter(courses, course =>
      _.some(course.grades, grade => {
        const uid = _.get(grade, "globalGrade.uid");
        return uid === "Grade 11" || uid === "Grade 12";
      })
    );
  }

  const { t } = useI18n();
  // After curriculum loads, grades still need to load before course queries can run.
  const loadingGrades = !!selectedCurriulumId && !selectedGradeId;
  const showLoader = isLoading || loadingCurriculums || loadingGrades;
  const showEmptyState =
    _.isEmpty(courses) && !isLoading && !loadingCurriculums && !loadingGrades;
  const showCourseList = !_.isEmpty(courses);

  useHandleScroll({
    networkStatus,
    loadMoreFeed,
    hasNextPage,
    getScrollRef: () => containerRef.current,
  });

  const emptyStateComponent = (
    <EmptyState
      illustration={NoSearchResultsIllustration}
      size="small"
      title={
        isToddleLibrary2_0
          ? t("common:no_course_found_plural")
          : t("common:no_class_found_plural")
      }
      subtitle={t("common:no_search_msg_label", {
        label: isToddleLibrary2_0
          ? t("common:learning_course_lowercase_plural")
          : t("common:classes"),
      })}
    />
  );

  const renderCourseItems = () => {
    return (
      <KeyboardNavigableList listContainerRef={listContainerRef}>
        <div ref={listContainerRef}>
          {_.map(courses, course => {
            const { id, title, grades, subjects } = course;
            const isSelected = _.includes(selectedIds, id);

            const onCourseSelection = () => {
              handleSelect({ course });
            };

            return (
              <CourseItem
                key={id}
                id={id}
                title={title}
                grades={grades}
                shouldDisplaySubjects={shouldDisplaySubjects}
                subjects={subjects}
                isSelected={isSelected}
                handleSelect={onCourseSelection}
                isTermsEnabled={isTermsEnabled}
                t={t}
              />
            );
          })}
        </div>
      </KeyboardNavigableList>
    );
  };

  const courseListComponent = () => {
    const columnHeaders = [
      isToddleLibrary2_0
        ? t("common:course_name")
        : t("common:class_name", { name: "name" }),
      t("common:grade_plural"),
    ];

    if (shouldDisplaySubjects) {
      columnHeaders.push(t("common:subject_plural"));
    }

    const columnHeaderClasses = index =>
      classNames("heading-6", classes.title, {
        [classes.firstColumnHeader]: index === 0,
      });

    const courseItemClasses = classNames(classes.courseItem, {
      [classes.courseItemWithSubjects]: shouldDisplaySubjects,
    });

    return (
      <>
        <div className={courseItemClasses}>
          {_.map(columnHeaders, (header, index) => (
            <div key={index} className={columnHeaderClasses(index)}>
              {header}
            </div>
          ))}
        </div>
        {renderCourseItems()}
      </>
    );
  };

  return (
    <div className={classes.container}>
      {showEmptyState ? emptyStateComponent : null}
      {showCourseList ? courseListComponent() : null}
      {showLoader ? <Loading /> : null}
      {/** This button is required for accessibility, else focus doesn't move to radio buttons due to focus-trapping */}
      <button className={classes.dummyBtn}>.</button>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    searchText = "",
    selectedCurriulum,
    fetchTemplates = false,
    selectedGradeId,
    selectedSubjectId,
    academicYears,
    courseSubjectType,
    subjects,
  } = ownProps;

  const selectedCurriulumId = _.get(selectedCurriulum, "id");

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const userId = platformUserInfo.id;
  const orgId = platformUserInfo.org_id;
  const isToddleLibrary = isToddleLibraryRoute();
  const globalFilter = _.get(state, "toddleLibrary.globalFilter", {});
  const globalGradeLabel = _.get(globalFilter, "grade.label", "");
  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const isToddleLibrary2_0 = isLearningCoursesEnabled && isToddleLibrary;
  const isDPLibrary = isToddleLibrary
    ? _.isEqual(globalGradeLabel, "Grade 11 & 12")
    : false;

  const isCurriculumTypeDP = _.isEqual(
    _.get(selectedCurriulum, "type"),
    CURRICULUM_TYPE_DP
  );

  const hasLibrarySuperAdminAccess = checkHasLibrarySuperAdminAccess();

  const curriculumProgramsWithAllClassAccess = getCurriculumProgramsWithAllClassAccess(
    state
  );

  const hasAllClassAccess = _.some(
    curriculumProgramsWithAllClassAccess,
    program => program?.id === selectedCurriulumId
  );

  const shouldShowOrganizationCourses =
    hasLibrarySuperAdminAccess && hasAllClassAccess;

  const staffCoursesQueryVariables = {
    id: userId,
    filters: {
      searchText,
      curriculumProgramIds: selectedCurriulumId ? [selectedCurriulumId] : [],
      ...(!isToddleLibrary2_0
        ? { grades: selectedGradeId !== "all" ? [selectedGradeId] : [] }
        : {}),
      ...(!isToddleLibrary2_0
        ? {
            subjectIds:
              selectedSubjectId !== "all" && selectedSubjectId
                ? [selectedSubjectId]
                : [],
          }
        : {}),
      academicYears: !_.isEmpty(academicYears) ? academicYears : null,
      ...(!isToddleLibrary2_0 ? { subjectTypes: courseSubjectType ?? [] } : {}),
    },
    courseGradeFilters: isToddleLibrary2_0
      ? { academicYearIds: [_.first(academicYears)] }
      : { academicYearId: _.first(academicYears) },
    fetchTemplates,
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header
  };

  const subjectsList = _.map(subjects, subject => _.get(subject, "id"));

  const curriculumCoursesQueryVariables = {
    id: orgId,
    filters: {
      searchText,
      curriculumProgramIds: selectedCurriulumId ? [selectedCurriulumId] : [],
      grades: selectedGradeId !== "all" ? [selectedGradeId] : [],
      subjects:
        selectedSubjectId && selectedSubjectId !== "all"
          ? [selectedSubjectId]
          : isCurriculumTypeDP
          ? subjectsList
          : [],
      academicYears: !_.isEmpty(academicYears) ? academicYears : null,
      // subjectTypes: courseSubjectType ?? [], // v1 doesn't have it
    },
    courseGradeFilters: isToddleLibrary2_0
      ? { academicYearIds: [_.first(academicYears)] }
      : { academicYearId: _.first(academicYears) },
    fetchTemplates,
    portalType: "PLANNER", // Need to pass this to send platform user JWT in request header
  };

  return {
    userId,
    orgId,
    staffCoursesQueryVariables,
    curriculumCoursesQueryVariables,
    selectedCurriulumId,
    selectedGradeId,
    isDPLibrary,
    shouldShowOrganizationCourses,
    isLoading: false,
    isToddleLibrary2_0,
  };
};

const mapActionCreators = { checkTermsExistsForSelectedCurriculumProgram };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  graphql(getStaffCoursesQuery, {
    name: "getStaffCourses",
    skip: ({
      userId,
      selectedCurriulumId,
      selectedGradeId,
      shouldShowOrganizationCourses,
      isToddleLibrary2_0,
    }) =>
      !userId ||
      !selectedCurriulumId ||
      !selectedGradeId ||
      shouldShowOrganizationCourses ||
      isToddleLibrary2_0,
    options: ({ staffCoursesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: staffCoursesQueryVariables,
    }),
    props({
      getStaffCourses,
      ownProps: { staffCoursesQueryVariables, isLoading },
    }) {
      const queryData = getStaffCoursesQueryFromCache(
        staffCoursesQueryVariables
      );

      const courseEntities = _.get(queryData, "coursesV2");
      const courseEdges = _.get(courseEntities, "edges");
      const courses = _.map(courseEdges, "node");

      const hasNextPage = _.get(courseEntities, "pageInfo.hasNextPage", false);
      const endCursor = _.get(courseEntities, "pageInfo.endCursor", null);

      return {
        courses,
        hasNextPage,
        networkStatus: getStaffCourses.networkStatus,
        isLoading:
          isLoading || _.includes([1, 2, 3], getStaffCourses.networkStatus),
        loadMoreFeed: prepareLoadMoreFeed({
          query: getStaffCoursesQuery,
          variables: {
            ...staffCoursesQueryVariables,
            after: endCursor,
          },
          queryData: getStaffCourses,
          customPathString: "node.coursesV2",
        }),
      };
    },
  }),
  graphql(getCurriculumCoursesQuery, {
    name: "getCurriculumCourses",
    skip: ({
      orgId,
      selectedCurriulumId,
      selectedGradeId,
      shouldShowOrganizationCourses,
      isToddleLibrary2_0,
    }) =>
      !orgId ||
      !selectedCurriulumId ||
      !selectedGradeId ||
      !shouldShowOrganizationCourses ||
      isToddleLibrary2_0,
    options: ({ curriculumCoursesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: curriculumCoursesQueryVariables,
    }),
    props({
      getCurriculumCourses,
      ownProps: { curriculumCoursesQueryVariables, isLoading },
    }) {
      const queryData = _.get(getCurriculumCourses, "node", {});

      const courseEntities = _.get(queryData, "courses");
      const courseEdges = _.get(courseEntities, "edges", []);
      const courses = _.map(courseEdges, "node");

      const hasNextPage = _.get(courseEntities, "pageInfo.hasNextPage", false);
      const endCursor = _.get(courseEntities, "pageInfo.endCursor", null);

      return {
        courses,
        hasNextPage,
        networkStatus: getCurriculumCourses.networkStatus,
        isLoading:
          isLoading ||
          _.includes([1, 2, 3], getCurriculumCourses.networkStatus),
        loadMoreFeed: prepareLoadMoreFeed({
          query: getCurriculumCoursesQuery,
          variables: {
            ...curriculumCoursesQueryVariables,
            after: endCursor,
          },
          queryData: getCurriculumCourses,
          customPathString: "node.courses",
        }),
      };
    },
  }),
  graphql(getCurriculumLearningCoursesQuery, {
    name: "getCurriculumLearningCourses",
    skip: ({
      orgId,
      selectedCurriulumId,
      selectedGradeId,
      isToddleLibrary2_0,
    }) =>
      !orgId || !selectedCurriulumId || !selectedGradeId || !isToddleLibrary2_0,
    options: ({ curriculumCoursesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: curriculumCoursesQueryVariables,
    }),
    props({
      getCurriculumLearningCourses,
      ownProps: { curriculumCoursesQueryVariables, isLoading },
    }) {
      const queryData = _.get(getCurriculumLearningCourses, "node", {});

      const courseEntities = _.get(queryData, "learningCourses");
      const courseEdges = _.get(courseEntities, "edges", []);
      const courses = _.map(courseEdges, "node");
      const hasNextPage = _.get(courseEntities, "pageInfo.hasNextPage", false);
      const endCursor = _.get(courseEntities, "pageInfo.endCursor", null);

      return {
        courses,
        hasNextPage,
        networkStatus: getCurriculumLearningCourses?.networkStatus,
        isLoading:
          isLoading ||
          _.includes([1, 2, 3], getCurriculumLearningCourses?.networkStatus),
        loadMoreFeed: prepareLoadMoreFeed({
          query: getCurriculumLearningCoursesQuery,
          variables: {
            ...curriculumCoursesQueryVariables,
            after: endCursor,
          },
          queryData: getCurriculumLearningCourses,
          customPathString: "node.learningCourses",
        }),
      };
    },
  })
)(CourseList);
